export const LEGENDARIUM_KEY = 'dd_legendarium_v1';

// When legendarium data changes, this custom event is dispatched on the window.
// Screens can listen to this event instead of polling localStorage.
export const LEGENDARIUM_EVENT = 'legendarium-state-changed';

function asStringArray(value) {
  return Array.isArray(value)
    ? value.filter((entry) => typeof entry === 'string' && entry.trim().length > 0)
    : [];
}

function uniqSorted(value) {
  return [...new Set(asStringArray(value).map((entry) => entry.trim()))].sort((a, b) =>
    a.localeCompare(b)
  );
}

function createEmptyLegendariumState() {
  return {
    enemies: [],
    relics: [],
    cards: [],
    tarot: [],
    lore: [],
    storylets: [],
  };
}

// Emit a custom event signalling that the legendarium state has changed. This uses
// window.dispatchEvent so that listeners in other tabs or components can react to
// changes without requiring polling. It guards against running in non-browser
// environments.
function emitLegendariumStateChanged() {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent(LEGENDARIUM_EVENT));
  } catch {
    // ignore
  }
}

function sanitizeLegendariumState(value) {
  if (!value || typeof value !== 'object') return createEmptyLegendariumState();

  return {
    enemies: uniqSorted(value.enemies),
    relics: uniqSorted(value.relics),
    cards: uniqSorted(value.cards),
    tarot: uniqSorted(value.tarot),
    lore: uniqSorted(value.lore),
    storylets: uniqSorted(value.storylets),
  };
}

export function readLegendariumState() {
  try {
    const raw = localStorage.getItem(LEGENDARIUM_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return sanitizeLegendariumState(parsed);
  } catch {
    return createEmptyLegendariumState();
  }
}

export function writeLegendariumState(nextState) {
  try {
    // Normalize the input once to avoid creating new objects unnecessarily on
    // every write. This prevents unnecessary rerenders when the state has not
    // actually changed.
    const sanitized = sanitizeLegendariumState(nextState);
    localStorage.setItem(LEGENDARIUM_KEY, JSON.stringify(sanitized));
    emitLegendariumStateChanged();
  } catch {
    // ignore
  }
}

export function unlockLegendariumEntries(payload = {}) {
  const current = readLegendariumState();
  const nextState = {
    enemies: uniqSorted([...current.enemies, ...asStringArray(payload.enemies)]),
    relics: uniqSorted([...current.relics, ...asStringArray(payload.relics)]),
    cards: uniqSorted([...current.cards, ...asStringArray(payload.cards)]),
    tarot: uniqSorted([...current.tarot, ...asStringArray(payload.tarot)]),
    lore: uniqSorted([...current.lore, ...asStringArray(payload.lore)]),
    storylets: uniqSorted([...current.storylets, ...asStringArray(payload.storylets)]),
  };

  const changed =
    nextState.enemies.length !== current.enemies.length ||
    nextState.relics.length !== current.relics.length ||
    nextState.cards.length !== current.cards.length ||
    nextState.tarot.length !== current.tarot.length ||
    nextState.lore.length !== current.lore.length ||
    nextState.storylets.length !== current.storylets.length;

  if (changed) {
    writeLegendariumState(nextState);
  }

  return nextState;
}

export function clearLegendariumState() {
  try {
    localStorage.removeItem(LEGENDARIUM_KEY);
    emitLegendariumStateChanged();
  } catch {
    // ignore
  }
}
